/**
 * TopPage コンポーネント (SC-01)
 *
 * トップページ。ヒーロー画像とナビゲーションカードを表示。
 *
 */

import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { useI18n } from '@/i18n/useI18n';

/**
 * ナビゲーションカード定義
 */
interface NavigationCard {
  key: string;
  href: string;
}

const navigationCards: NavigationCard[] = [
  { key: 'navigationCards.works', href: '/works' },
  { key: 'navigationCards.timeline', href: '/timeline' },
  { key: 'navigationCards.events', href: '/events' },
  { key: 'navigationCards.about', href: '/about' },
];

/**
 * TopPage コンポーネント
 *
 * 使用例:
 * ```tsx
 * <TopPage />
 * ```
 */
export function TopPage() {
  const { t } = useI18n();
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main id="main-content" className="flex-1">
        {/* メインコンテンツ: 左側に画像、右側にナビゲーションカード */}
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
              {/* 左側: ヒーロー画像 */}
              <motion.div
                initial={shouldReduceMotion ? false : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center justify-center"
              >
                <div className="relative w-full max-w-lg">
                  <img
                    src="/images/hero.jpg"
                    alt={t('topPage.heroTitle')}
                    className="h-auto w-full rounded-lg"
                  />
                </div>
              </motion.div>

              {/* 右側: ナビゲーションカード (2x2 グリッド) */}
              <div className="grid gap-4 sm:grid-cols-2">
                {navigationCards.map((card, index) => (
                  <motion.div
                    key={card.href}
                    initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: shouldReduceMotion ? 0 : index * 0.1 + 0.2,
                    }}
                  >
                    <Link
                      to={card.href}
                      aria-label={t(`${card.key}.ariaLabel`)}
                      className="group flex h-full flex-col overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                      <div className="flex-1 p-4 md:p-6">
                        {/* タイトル */}
                        <h2 className="mb-2 text-lg font-semibold text-foreground transition-colors group-hover:text-primary md:text-xl">
                          {t(`${card.key}.title`)}
                        </h2>

                        {/* 説明 */}
                        <p className="text-xs text-muted-foreground md:text-sm">
                          {t(`${card.key}.description`)}
                        </p>
                      </div>

                      {/* ホバー時の矢印アイコン */}
                      <div className="border-t bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground md:px-6 md:py-3 md:text-sm">
                        {t('common.learnMore')} →
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
